import $ from 'jquery';
import WOW from 'wow.js';
import Vue from 'vue';

import ThemeBase, { TOGGLE_RIGHT_DRAWER_EVENT, TOGGLE_LEFT_DRAWER_EVENT, CART_UPDATE_EVENT } from './ThemeBase'
import Section from './Section';
// import Popup from './global/Popup';
// import Newsletter from './global/Newsletter';
// import CartDrawer from './global/CartDrawer';
// import Drawer from './global/Drawer';

// import LoginPage from './pages/LoginPage';

import Header from './sections/Header';
import Footer from './sections/Footer';
import AnnouncementBar from './sections/AnnouncementBar';
import AnimatedText from './sections/AnimatedText';
import CustomHtml from './sections/CustomHtml';
import Hero from './sections/Hero';
import BlogPosts from './sections/BlogPosts';
import CollectionList from './sections/CollectionList';
import EventsCalendar from './sections/EventsCalendar';
import FeaturedCollection from './sections/FeaturedCollection';
import FeaturedMenu from './sections/FeaturedMenu';
import ProductsBlock from './sections/ProductsBlock';
import FooterNewsletter from './sections/FooterNewsletter';
import ImageGallery from './sections/ImageGallery';
import ImageWithTextOverlay from './sections/ImageWithTextOverlay';
import ImageWithText from './sections/ImageWithText';
import LogoList from './sections/LogoList';
import Slideshow from './sections/Slideshow';
import Newsletter from './sections/Newsletter';
import Password from './sections/Password';
import FeaturedProduct from './sections/FeaturedProduct';
import CollectionSection from './sections/CollectionSection';
import CollectionListSection from './sections/CollectionListSection';
import ProductSection from './sections/ProductSection';
import RichText from './sections/RichText';
import Testimonials from './sections/Testimonials';
import Blog from './sections/Blog';
import TextAdverts from './sections/TextAdverts';
import VideoHero from './sections/VideoHero';
import Cart from './sections/Cart';

import Dropdown from './components/Dropdown.vue';
import Video from './components/Video.vue'

import Login from './global/Login';
import RightDrawer from './global/RightDrawer';
import Popup from './global/Popup';
import VerificationPopup from './global/VerificationPopup';
import { throttle } from './utils';
import Badge from './global/Badge';
import RecentlyViewed from './global/RecentlyViewed';
import InfoColumns from './global/InfoColumns';
import SearchResults from './global/SearchResults';

/**
 * @typedef {Object} ShopifyEventDetail
 * @property {string} sectionId
 * @property {string} [blockId]
 * @property {boolean} [load]
 */

/**
 * @typedef {CustomEvent<ShopifyEventDetail>} ShopifyEvent
 */

class Theme extends ThemeBase {
  THROTTLE_INTERVAL = 400;

  SECTIONS = {
    'header': Header,
    'footer-main': Footer,
    'announcement-bar': AnnouncementBar,
    'animated-text': AnimatedText,
    'advanced-layout--custom-html': CustomHtml,
    'advanced-layout--hero': Hero,
    'blog--blog-posts': BlogPosts,
    'collage-builder': VideoHero,
    'template--product--info-columns': InfoColumns,
    'collection--collection-list': CollectionList,
    'collection--featured-collection': FeaturedCollection,
    'collection--products-block': ProductsBlock,
    'countdown-timer': ImageWithText,
    'events-calendar': EventsCalendar,
    'image--gallery': ImageGallery,
    'image--image-with-text-overlay': ImageWithTextOverlay,
    'image--image-with-text': ImageWithText,
    'image--logo-list': LogoList,
    'image--slideshow': Slideshow,
    'newsletter--newsletter': Newsletter,
    'password': Password,
    'products--featured-product': FeaturedProduct,
    'template--blog': Blog,
    'template--cart': Cart,
    'template--collection': CollectionSection,
    'template--collections-list': CollectionListSection,
    'template--product': ProductSection,
    'template--product--recently-viewed': RecentlyViewed,
    'template--search': SearchResults,
    'text--rich-text': RichText,
    'text--testimonials': Testimonials,
    'text--text-adverts': TextAdverts,
    'video--video-hero': VideoHero,
    'video--video': Section,
    'featured-menu': FeaturedMenu,
  }

  VUE_COMPONENTS = {
    'wetheme-dropdown': Dropdown,
    'wetheme-video': Video,
  }

  constructor() {
    super();

    this.name = 'Flow';

    this.$ = $;

    // Passive event listeners for jQuery.
    $.event.special.touchstart = {
      setup: function(_, ns, handle) {
        // @ts-ignore
        this.addEventListener('touchstart', handle, {
          passive: !ns.includes('noPreventDefault')
        });
      }
    };
    $.event.special.touchmove = {
      setup: function(_, ns, handle) {
        // @ts-ignore
        this.addEventListener('touchmove', handle, {
          passive: !ns.includes('noPreventDefault')
        });
      }
    };

    /** Section by it's unique ID
     * @type {Map<string, Section>}
     */
    this.sectionById = new Map();

    /** Array of sections by given section type
     * @type {Map<string, Section[]>}
     */
    this.sectionsByType = new Map();

    /** Array of registed components
     * @type {Array<import('./Component').default>}
     */
    this.registeredComponents = [];

    /** Array of initialized vue components
     * @type {Array<Object>}
     */
    this.vueComponents = [];

    /** @type {Array<{ resolve: (YoutubeApi) => void, reject: (string) => void }>} */
    this.youtubeLoadedCallbacks = [];
    /** @type {Array<{ resolve: (VimeoApi) => void, reject: (string) => void }>} */
    this.vimeoLoadedCallbacks = [];

    this.resizeDebounceTimer = null;

    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:unload', this.onSectionUnload);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.addEventListener('shopify:section:reorder', this.onSectionReorder);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
    document.addEventListener('DOMContentLoaded', this.onLoadAll);
    document.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('resize', this.onRawResize);
    window.addEventListener('scroll', this.onRawScroll);

    this.drawerOverlay = document.querySelector('#DrawerOverlay');
    if (this.drawerOverlay) {
      // We don't have it on password page
      document.querySelector('#DrawerOverlay').addEventListener('click', this.closeDrawers);
    }

    // Number of attempts to load Shopify product reviews
    this._spr_remaining_attemtps = 10;
  }

  /** This will be execute when whole DOM is loaded */
  load() {
    this.loadGlobal();

    this.breakpoint = this.getBreakpoint();

    this.wow = new WOW().init();

    if (!document.body.classList.contains('password-page')) {
      // this.drawer = new Drawer(this);
      // this.cartDrawer = this.drawer.slideouts.right ? new CartDrawer(this, this.drawer.slideouts.right.menu) : null;
    }

    this.popup = null;
    const popupEl = /** @type {HTMLElement} */ (document.querySelector('#popup'));
    if (popupEl && window.location.pathname !== '/challenge') {
      // Don't show popup when we're filling CAPTCHA for contact form
      this.popup = new Popup(this, popupEl);
    }

    this.verificationPopup = null;
    const verificationEl = /** @type {HTMLElement} */ (document.querySelector('.verification-popup-container'));
    if (verificationEl && window.location.pathname !== '/challenge') {
      // Don't show popup when we're filling CAPTCHA for contact form
      this.verificationPopup = new VerificationPopup(this, verificationEl);
    }

    // Newsletter form could be on contact-us page, so outside of sections
    // this.newsletter = new Newsletter(this);

    // Login page doesn't have sections, load it here
    if (document.body.classList.contains('template-customers-login')) {
      this.login = new Login(this, document.body);
    }

    // Remove 'fadeInUp' animation from the first section
    let getAnimatedEls = document.querySelectorAll('.show-on-scroll');
    if (getAnimatedEls.length > 0) {
      getAnimatedEls[0].classList.remove('show-on-scroll');
    }

    this.handleAccordions();

    this.loadFadeAnimation();

    // Right drawer is not part of any component
    this.loadVueComponent(document.body, 'wetheme-right-drawer', RightDrawer);

    this.badge = new Badge(this, document.body);

    document.body.addEventListener('mousedown', this._usingMouse);
    document.body.addEventListener('keydown', this._usingTab);
  }

  _usingMouse() {
    document.body.classList.add('using-mouse');
    document.body.classList.remove('using-keyboard');
  }

  _usingTab(evt) {
    if (evt.key === "Tab") {
      document.body.classList.remove('using-mouse');
      document.body.classList.add('using-keyboard');
    }
  }

  loadFadeAnimation = () => {
    document.body.classList.add('loaded');

    if (navigator.userAgent.indexOf('Safari') != -1 &&
      navigator.userAgent.indexOf('Chrome') == -1) {
      // Safari doesn't support beforeunload very well when using back/forward buttons.
      return;
    }

    window.addEventListener('beforeunload', function () {
      document.body.classList.add('unloading');
    });
  }

  loadGlobal = () => {
    const globalEl = document.querySelector('#wetheme-global');
    if (!globalEl) {
      console.warn('Script #wetheme-global with global definitions not found');
      return;
    }

    let global;
    try {
      global = JSON.parse(globalEl.textContent);
    } catch (e) {
      console.warn('Invalid syntax in #wetheme-global', e);
      return;

    }
    this.translations = global.translations;
    this.moneyFormat = global.moneyFormat;
    this.giftwrapProduct = global.giftwrapProduct;
    this.giftwrapEnabled = global.giftwrapEnabled;

    Vue.directive('money', {
      bind: (el, binding) => {
        el.innerHTML = this.formatMoney(binding.value);
      },
      update: (el, binding) => {
        if (binding.oldValue !== binding.value) {
          el.innerHTML = this.formatMoney(binding.value);
        }
      },
    });

    Vue.directive('responsive', {
      bind: (el, binding) => {
        /* Create product thumbnail:
         *  - Remove file extension, add _1000x, and re-add extension
         *  - Create server relative link
         *  - Create responsive variant (`_{width}x` as size)
         */

        let srcset = '';

        if(el.width > 550) {
          srcset += binding.value.replace(/(\.[^.]*)$/, "_550x$1") + ' 550w,';
        }
        if(el.width > 1100) {
          srcset += binding.value.replace(/(\.[^.]*)$/, "_1100x$1") + ' 1100w,';
        }
        if(el.width > 1445) {
          srcset += binding.value.replace(/(\.[^.]*)$/, "_1445x$1") + ' 1445w,';
        }
        if(el.width > 1680) {
          srcset += binding.value.replace(/(\.[^.]*)$/, "_1680x$1") + ' 1680w,';
        }
        if(el.width > 2048) {
          srcset += binding.value.replace(/(\.[^.]*)$/, "_2048x$1") + ' 2048w,';
        }

        // Plus the original image size for good measure.
        srcset += binding.value + ' ' + el.width + 'w';

        const img = binding.value.replace(/(\.[^.]*)$/, "_1000x$1").replace('http:', '');
        el.setAttribute('src', img);
        el.setAttribute('srcset', srcset);
        el.setAttribute('loading', 'lazy');
        el.setAttribute('loading', 'lazy');

      },
    });
  }

  /**
   * Load vue components in `element` tree
   *
   * @param {HTMLElement} element
   */
  loadVueComponents = (element) => {
    Object.entries(this.VUE_COMPONENTS).forEach(
      ([tagName, VueComponent]) => {
        this.loadVueComponent(element, tagName, VueComponent);
      },
    );
  }

  /**
   * Load all instances of one vue component
   *
   * @param {HTMLElement} element Element which children should be searched for vue components
   * @param {string} tagName
   * @param {*} Component
   */
  loadVueComponent = (element, tagName, Component) => {
    Array.prototype.forEach.call(
      element.querySelectorAll(tagName),
      (el) => {
        const c = new Vue({
          el: el.parentNode,
          components: {
            [tagName]: Component,
          },
          data: () => {
            return {
              theme: this,
            };
          },
        });
        this.vueComponents.push(c);
      }
    );
  }

  /**
   * Load section
   *
   * @param {HTMLElement} element Element to attach the section to
   * @param {typeof Section} SectionClass Section class to load
   * @param {boolean} pageLoad True on initial page load, False when the section reloads
   */
  loadSection = (element, SectionClass, pageLoad) => {
    const c = new SectionClass(this, element, pageLoad);
    this.sectionById.set(element.dataset.wethemeSectionId, c);
    const type = element.dataset.wethemeSectionType;
    let sections = this.sectionsByType.get(type);
    if (!sections) {
      sections = [];
      this.sectionsByType.set(type, sections);
    }
    sections.push(c);
    this.loadVueComponents(element);
  }

  /**
   * Load all sections that has given type
   *
   * @param {string} type Type of section to laod
   * @param {typeof Section} SectionClass Section class to load
   * @param {boolean} pageLoad True on initial page load, False when the section reloads
   */
  loadSectionsByType = (type, SectionClass, pageLoad) => {
    const selector = `[data-wetheme-section-type="${type}"]`;
    Array.prototype.forEach.call(
      document.querySelectorAll(selector),
      (element) => {
        try {
          this.loadSection(element, SectionClass, pageLoad);
        } catch (e) {
          console.error(`Unable to load section ${type} on element`, element, e);
        }
      },
    );
  }

  /**
   * Load all the sections
   */
  onLoadAll = () => {
    this.load();
    Object.entries(this.SECTIONS).forEach(
      ([type, SectionClass]) => {
        this.loadSectionsByType(type, SectionClass, true);
      },
    );
    this.showOnScroll();
  }

  /**
   * This is called when section loads in Theme Editor, it'll initialize section by the sectionId param
   *
   * @param {ShopifyEvent} event
   */
  onSectionLoad = (event) => {
    const { sectionId } = event.detail;
    const selector = `[data-wetheme-section-id="${sectionId}"]`;
    const element = /** @type {HTMLElement} */ (document.querySelector(selector));
    if (!element) {
      console.warn(`Element with ${selector} not found, section won't be loaded`);
      return;
    }
    const type = element.dataset.wethemeSectionType;
    const SectionClass = this.SECTIONS[type];
    if (!SectionClass) {
      console.warn(`Cannot find class with type "${type}"`);
      return;
    }
    // Remove 'fadeInUp' animation from the first section
    let getAnimatedEls = document.querySelectorAll('.show-on-scroll');
    if (getAnimatedEls.length > 0) {
      getAnimatedEls[0].classList.remove('show-on-scroll');
    }
    this.loadSection(element, SectionClass, false);
    this.showOnScroll();
  }

  /**
   * This is called when section unloads in Theme Editor, it'll destroy a section by the sectionId param
   *
   * @param {ShopifyEvent} event
   */
  onSectionUnload = (event) => {
    const element = /** @type {HTMLElement} */ (event.target);
    const { sectionId } = event.detail;
    const section = this.sectionById.get(sectionId);
    if (!section) {
      console.warn(`No section with id ${sectionId}, nothing will be unloaded`);
      return;
    }
    section.destroy({ element });
  }

  /**
   * This is called when section is selected (opened) by user in Theme Editor
   *
   * @param {ShopifyEvent} event
   */
  onSectionSelect = (event) => {
    const element = /** @type {HTMLElement} */ (event.target);
    const { sectionId, load } = event.detail;
    const section = this.sectionById.get(sectionId);
    if (!section) {
      console.warn(`No section with id ${sectionId}, nothing will be selected`);
      return;
    }
    section.onSectionSelect({ element, sectionId, load });
  }

  /**
   * This is called when section is deselected (closed) by user in Theme Editor
   *
   * @param {ShopifyEvent} event
   */
  onSectionDeselect = (event) => {
    const element = /** @type {HTMLElement} */ (event.target);
    const { sectionId } = event.detail;
    const section = this.sectionById.get(sectionId);
    if (!section) {
      console.warn(`No section with id ${sectionId}, nothing will be deselected`);
      return;
    }
    section.onSectionDeselect({ element, sectionId });
  }

  /**
   * This is called when section is reorderd by user in Theme Editor
   *
   * @param {ShopifyEvent} event
   */
  onSectionReorder = (event) => {
    const element = /** @type {HTMLElement} */ (event.target);
    const { sectionId } = event.detail;
    const section = this.sectionById.get(sectionId);
    if (!section) {
      console.warn(`No section with id ${sectionId}, nothing will be done on reorder`);
      return;
    }
    section.onSectionReorder({ element, sectionId });
  }

  /**
   * This is called when block is selected (opened) by user in Theme Editor
   *
   * @param {ShopifyEvent} event
   */
  onBlockSelect = (event) => {
    const element = /** @type {HTMLElement} */ (event.target);
    const { sectionId, blockId, load } = event.detail;
    const section = this.sectionById.get(sectionId);
    if (!section) {
      console.warn(`No section with id ${sectionId}, block select will do nothing`);
      return;
    }
    section.onBlockSelect({ element, sectionId, blockId, load });
  }

  /**
   * This is called when block is deselected (closed) by user in Theme Editor
   *
   * @param {CustomEvent} event
   */
  onBlockDeselect = (event) => {
    const element = /** @type {HTMLElement} */ (event.target);
    const { sectionId, blockId } = event.detail;
    const section = this.sectionById.get(sectionId);
    if (!section) {
      console.warn(`No section with id ${sectionId}, block deselect will do nothing`);
      return;
    }
    section.onBlockDeselect({ element, sectionId, blockId });
  }

  /**
   * This is called immediatelly when window is resized
   *
   * @param {Event} event
   */
  onRawResize = (event) => {
    const width = this.getWindowWidth();

    // Trigger non-debounced event on all components
    this.registeredComponents.forEach(
      (c) => c.onWindowResizeRaw({ width })
    )

    this.onResize();
  }

  /**
   * This is called regularly when resized (throttled)
   */
  onResize = throttle(() => {
    const width = this.getWindowWidth();
    const oldWidth = this.previousWidth;
    const breakpoint = this.getBreakpoint(width);
    this.registeredComponents.forEach(
      (c) => {
        c.onWindowResize({ width, oldWidth, breakpoint });
        if (breakpoint !== this.breakpoint) {
          // Breakpoint has changed, trigger the event
          c.onWindowResizeBreakpoint({ breakpoint, oldBreakpoint: this.breakpoint, width });
        }
      }
    );
    this.previousWidth = width;
    this.breakpoint = breakpoint;
  }, this.THROTTLE_INTERVAL);

  /**
   * This is called immediatelly when scrolled
   *
   * @param {Event} event
   */
  onRawScroll = (event) => {
    this.onScroll(event);
  }

  /**
   * This is called regularly when scrolled (throttled)
   *
   * @param {Event} event
   */
  onScroll = throttle((event) => {
    this.showOnScroll();
    this.registeredComponents.forEach(
      (c) => {
        c.onWindowScroll();
      },
    );
  }, this.THROTTLE_INTERVAL);

  showOnScroll = () => {
    const windowHeight = $(window).height();
    $('.show-on-scroll').each(function (index, section) {
      var rect = section.getBoundingClientRect();
      if (rect.bottom >= 0 && rect.top < windowHeight) {
        $(section).removeClass('show-on-scroll').addClass('shown-on-scroll animated fadeInUp');
      }
    });
  }

  /**
   * Handle keyup on document
   *
   * @param {KeyboardEvent} e
   */
  onKeyUp = (e) => {
    // Close drawers when escape is pressed if they are open
    if (e.keyCode == 27) { // escape key maps to keycode `27`
      this.closeDrawers()
    }
  }

  handleAccordions = () => {
    let detailsElements = document.querySelectorAll('details');

    //@ts-ignore
    for (let item of detailsElements) {
      item.addEventListener('click', (e) => {
        const target = /** @type {HTMLElement} */ (e.currentTarget);

        setTimeout(() => {
          const isExpanded = item.open ? 'true' : 'false';

          item.firstElementChild.setAttribute('aria-expanded', isExpanded);
        }, 0);
      });
    }
  }

  loadYoutubeApi = () => {
    return new Promise((resolve, reject) => {
      if (window.YT) {
        // We already have youtube API loaded, we're done
        resolve(window.YT);
      } else if (!document.querySelector('#youtube_api')) {
        // Resolve once youtube is loaded
        this.youtubeLoadedCallbacks.push({ resolve, reject });

        const embedContainers = /** @type {NodeListOf<Element>} */ (document.querySelectorAll('.type-youtube'));

        const videoObserverOptions = {

        }

        const videoObserverCallback = (watchEntries, videoObserver) => {
          watchEntries.forEach(watchEntry => {
            if (watchEntry.isIntersecting) {
              // Youtube API is not yet loaded, create script that loads it
              this.createScript("youtube_api", "https://www.youtube.com/player_api", true);
              embedContainers.forEach(embedContainer => {
                videoObserver.unobserve(embedContainer);
              });
            }
          })
        }

        const videoObserver = new IntersectionObserver(videoObserverCallback, videoObserverOptions);
        
        if (embedContainers.length) {
          embedContainers.forEach(embedContainer => {
            videoObserver.observe(embedContainer);
          })
        }

        
      } else {
        // Script for loading youtube API is there, but youtube is not yet loaded
        this.youtubeLoadedCallbacks.push({ resolve, reject });
      }
    });
  }

  onYoutubeLoaded = () => {
    this.youtubeLoadedCallbacks.forEach(({ resolve }) => {
      resolve(window.YT);
    });
    this.youtubeLoadedCallbacks.splice(0);
  }

  loadVimeoApi = () => {
    return new Promise((resolve, reject) => {
      if (window.Vimeo) {
        // Already loaded
        resolve(window.Vimeo);
      } else if (!document.querySelector('#vimeo_api')) {
        // Vimeo API is not yet loaded, create script that loads it
        const script = this.createScript("vimeo_api", "https://player.vimeo.com/api/player.js");
        script.onload = () => {
          resolve(window.Vimeo);
          this.onVimeoLoaded();
        };
      } else {
        this.vimeoLoadedCallbacks.push({ resolve, reject });
      }
    });
  }

  onVimeoLoaded = () => {
    this.vimeoLoadedCallbacks.forEach(({ resolve }) => {
      resolve(window.Vimeo);
    });
    this.vimeoLoadedCallbacks.splice(0);
  }

  _loadCartKey = () => {
    const cerberusKey = 'f1957945-9048-438b-bd2c-0ed182340c65'
    return cerberusKey
  }

  toggleRightDrawer = (type = 'cart', forceOpen = undefined, params = {}) => {
    const event = new CustomEvent(TOGGLE_RIGHT_DRAWER_EVENT, {
      detail: {
        type,
        forceOpen,
        params,
      },
    });
    document.documentElement.dispatchEvent(event);
  }

  toggleLeftDrawer = (forceOpen = undefined) => {
    const event = new CustomEvent(TOGGLE_LEFT_DRAWER_EVENT, {
      detail: {
        forceOpen,
      },
    });
    document.documentElement.dispatchEvent(event);
  }

  updateCartDrawer = (cart) => {
    const event = new CustomEvent(CART_UPDATE_EVENT, {
      detail: {
        cart
      },
    });
    document.documentElement.dispatchEvent(event);
  }

  createScript = (id, src, async = false) => {
    const tag = document.createElement('script');
    tag.id = id;
    tag.src = src;
    if (async) {
      tag.defer = true;
      tag.async = true;
    }
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    return tag;
  }

  registerComponent = (component) => {
    this.registeredComponents.push(component);
  }

  makeVideoEmbedsResponsive = () => {
    $('.template-page iframe, .template-blog iframe, .template-article iframe').each(function (index, iframe) {
      var source = $(iframe).attr("src");

      if (source.indexOf("youtube.com") >= 0 || source.indexOf("vimeo.com") >= 0 || source.indexOf("dailymotion.com") >= 0) {
        //create container
        var embedContainer = document.createElement('div');
        $(embedContainer).addClass('embed-container');

        //clone iframe
        var iframeClone = $(iframe).clone();

        //put clone in container
        $(embedContainer).append(iframeClone);

        //embed clone with responsive container
        $(iframe).replaceWith(embedContainer);
      }
    });
  }

  closeDrawers = (e) => {
    this.toggleLeftDrawer(false);
    this.toggleRightDrawer('cart', false);
  }

  addBadges = (root, timeout) => {
    this.badge.add(root, timeout);
  }
}

// Make the theme object instance globally available (without overriding the existing window.wetheme)
const theme = new Theme();
for (const [key, value] of Object.entries(theme)) {
  window.wetheme[key] = value;
}

window.onYouTubePlayerAPIReady = () => {
  window.wetheme.onYoutubeLoaded();
};
